import React, { useEffect, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import {
    Card,
    CardContent,
    CardHeader,
    CardTitle,
    CardDescription,
} from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
    DialogDescription,
} from "./ui/dialog";
import {
    Shield,
    TrendingUp,
    Users,
    DollarSign,
    CheckCircle,
    Clock,
    Target,
    Star,
    Plus,
    Trash2,
    Award,
    BarChart3,
    UserPlus,
    AlertCircle,
    Eye,
} from "lucide-react";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "./ui/table";
import { toast } from "sonner";
import {
    useGetOperationsPerformanceData,
    useGetSalesPerformanceData,
    useGetRequestsPendingClosure,
    useGetAllUsersData,
    useGetAllInvoicesData,
    useGetAllPaymentsData,
    useGetAllIncentivesData,
} from "@/hooks/useOperationPerformanceData";
import {
    getOperationCompleteTotalRate,
    getOperationCompletionRate,
} from "@/utils/formatters";
import { api } from "@/utils/api";
import { useNavigate } from "react-router-dom";
const AdminPerformanceDashboard = () => {
    const { user } = useAuth();
    const {
        data: operationsPerformance,
        loading,
        error,
        refetch: refetchOps,
    } = useGetOperationsPerformanceData();

    const navigate = useNavigate();

    const {
        data: salesPerformance,
        loading: salesLoading,
        error: salesError,
        refetch: refetchSales,
    } = useGetSalesPerformanceData();

    const {
        data: pendingAndClosurePerformance,
        loading: pendingAndClosureLoading,
        error: pendingAndClosureError,
        refetch: refetchClosure,
    } = useGetRequestsPendingClosure();

    // Hardcoded data for salespeople
    const [salespeople, setSalespeople] = useState([]);

    // Hardcoded data for operations team
    const [operationsTeam, setOperationsTeam] = useState([]);
    const {
        data: usersData,
        loading: usersLoading,
        error: usersError,
    } = useGetAllUsersData();

    useEffect(() => {
        if (operationsPerformance) {
            setOperationsTeam(operationsPerformance);
        }
    }, [operationsPerformance]);

    useEffect(() => {
        if (salesPerformance) {
            setSalespeople(salesPerformance);
        }
    }, [salesPerformance]);

    // Hardcoded requests data
    const [requests, setRequests] = useState([]);

    const [selectedIncentives, setSelectedIncentives] = useState([]);

    // Modal states
    const [showCompleteModal, setShowCompleteModal] = useState(false);
    const [showAddMemberModal, setShowAddMemberModal] = useState(false);
    const [selectedRequest, setSelectedRequest] = useState(null);
    const [searchTerm, setSearchTerm] = useState("");
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);

    const {
        data: invoicesData,
        loading: invoicesLoading,
        error: invoicesError,
    } = useGetAllInvoicesData();

    const {
        data: paymentsData,
        loading: paymentsLoading,
        error: paymentsError,
    } = useGetAllPaymentsData();

    const {
        data: incentivesData,
        loading: incentivesLoading,
        error: incentivesError,
        refetch: refetchIncentives,
    } = useGetAllIncentivesData();

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedIncentiveId, setSelectedIncentiveId] = useState(null);

    const [invoiceList, setInvoiceList] = useState([]);
    const [payments, setPayments] = useState([]);

    const [incentives, setIncentives] = useState([]);

    const filteredInvoices = invoiceList.filter((invoice) =>
        invoice.client_name?.toLowerCase().includes(searchTerm.toLowerCase()),
    );

    const filteredPayments = payments.filter((payment) =>
        payment.client_name?.toLowerCase().includes(searchTerm.toLowerCase()),
    );

    const filteredRequests = requests.filter((request) =>
        request.customer_name?.toLowerCase().includes(searchTerm.toLowerCase()),
    );

    const filteredOperations = operationsTeam.filter((operation) =>
        operation.name?.toLowerCase().includes(searchTerm.toLowerCase()),
    );

    const filteredSales = salespeople.filter((sale) =>
        sale.name?.toLowerCase().includes(searchTerm.toLowerCase()),
    );

    useEffect(() => {
        if (invoicesData) {
            setInvoiceList(invoicesData);
        }
    }, [invoicesData]);

    useEffect(() => {
        if (paymentsData) {
            setPayments(paymentsData);
        }
    }, [paymentsData]);

    useEffect(() => {
        if (incentivesData) {
            setIncentives(incentivesData);
        }
    }, [incentivesData]);

    const filteredUsers =
        searchTerm.trim() === ""
            ? usersData
            : usersData?.filter((user) =>
                `${user.name} ${user.role}`
                    .toLowerCase()
                    .includes(searchTerm.toLowerCase()),
            );

    // Add member form
    const [newMember, setNewMember] = useState({
        name: "",
        email: "",
        role: "sales",
    });

    useEffect(() => {
        if (pendingAndClosurePerformance) {
            setRequests(pendingAndClosurePerformance);
        }
    });

    // Access control
    if (user?.role !== "admin") {
        return (
            <div className="flex items-center justify-center h-screen">
                <div className="text-center">
                    <Shield className="w-16 h-16 text-red-500 mx-auto mb-4" />
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">
                        Access Denied
                    </h2>
                    <p className="text-gray-600">
                        You don't have permission to access this page.
                    </p>
                </div>
            </div>
        );
    }

    // Handle mark request complete
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleMarkComplete = async () => {
        if (!selectedRequest) return;

        try {
            setIsSubmitting(true);

            const payload = {
                request_id: selectedRequest.request_id,
                incentives: selectedIncentives,
            };

            await api.confirmIncentive(payload);

            toast.success(
                `Request ${selectedRequest.request_id} marked complete successfully!`,
            );

            // Refresh data
            if (refetchOps) refetchOps();
            if (refetchSales) refetchSales();
            if (refetchClosure) refetchClosure();

            // Reset states
            setShowCompleteModal(false);
            setSelectedRequest(null);
            setSelectedIncentives([]);
        } catch (error) {
            console.error("Incentive confirmation failed:", error);
            toast.error("Failed to mark request complete. Please try again.");
        } finally {
            setIsSubmitting(false);
        }
    };
    const [commonAmount, setCommonAmount] = useState(5000);
    const handleSelectUser = (user_id) => {
        const alreadySelected = selectedIncentives.find(
            (u) => u.user_id === user_id,
        );

        if (alreadySelected) {
            setSelectedIncentives((prev) =>
                prev.filter((u) => u.user_id !== user_id),
            );
        } else {
            // Add if not selected
            setSelectedIncentives((prev) => [
                ...prev,
                {
                    user_id: user_id,
                    amount: commonAmount,
                    status: "paid",
                },
            ]);
        }
    };

    const handleRemoveUser = (id) => {
        setSelectedIncentives((prev) => prev.filter((u) => u.user_id !== id));
    };
    const handleIncentiveChange = (user_id, value) => {
        setCommonAmount(value);
        setSelectedIncentives((prev) =>
            prev.map((user) =>
                user.user_id === user_id
                    ? { ...user, amount: Number(value) }
                    : user,
            ),
        );
    };

    const findUser = (user_id) => {
        const user = usersData?.find((u) => u.user_id === user_id);
        return {
            name: user?.name || "",
            role: user?.role || "",
        };
    };
    // Handle add team member
    const handleAddMember = () => {
        if (!newMember.name || !newMember.email) {
            toast.error("Please fill in all fields");
            return;
        }

        const newId =
            newMember.role === "sales"
                ? Math.max(...salespeople.map((s) => s.id)) + 1
                : Math.max(...operationsTeam.map((o) => o.id)) + 1;

        const memberData = {
            id: newId,
            name: newMember.name,
            email: newMember.email,
            requestsHandled: 0,
            requestsProcessed: 0,
            conversionRate: 0,
            completionRate: 0,
            revenueGenerated: 0,
            avgResponseTime: "N/A",
            monthlyTarget: newMember.role === "sales" ? 3000000 : 70,
            customerSatisfaction: 0,
            totalIncentives: 0,
        };

        if (newMember.role === "sales") {
            setSalespeople((prev) => [...prev, memberData]);
        } else {
            setOperationsTeam((prev) => [...prev, memberData]);
        }

        toast.success(`${newMember.name} added to ${newMember.role} team!`);
        setShowAddMemberModal(false);
        setNewMember({ name: "", email: "", role: "sales" });
    };

    // Handle remove member
    const handleRemoveMember = (id, role) => {
        if (role === "sales") {
            setSalespeople((prev) => prev.filter((s) => s.id !== id));
        } else {
            setOperationsTeam((prev) => prev.filter((o) => o.id !== id));
        }
        toast.success("Team member removed successfully");
    };

    const handleDownload = async (invoice_id) => {
        try {
            const res = api.downloadProformaInvoice(invoice_id);
            console.log(res);
        } catch (err) {
            console.log(err);
        }
    };

    const handleMarkCompleteForIncentive = async () => {
        if (!selectedIncentiveId) return;
        try {
            await api.markIncentiveAsPaid(selectedIncentiveId);
            toast.success("Incentive marked as completed");
            refetchIncentives();
        } catch (error) {
            toast.error("Failed to mark as completed");
        } finally {
            setIsModalOpen(false);
            setSelectedIncentiveId(null);
        }
    };
    // Calculate overall stats
    const totalRevenue = salespeople.reduce(
        (sum, sp) => sum + sp.revenueGenerated,
        0,
    );
    const avgConversionRate =
        salespeople.reduce((sum, sp) => sum + sp.conversionRate, 0) /
        salespeople.length;
    const avgCompletionRate = getOperationCompleteTotalRate(operationsTeam);
    const totalIncentivesPaid = requests
        .filter((r) => r.status === "completed")
        .reduce((sum, r) => sum + (r.incentive || 0), 0);
    const pendingRequestsCount = requests.filter(
        (r) => r.status === "pending",
    ).length;

    const [activeTab, setActiveTab] = useState("requests");

    return (
        <div
            className="max-w-7xl mx-auto"
            data-testid="admin-performance-dashboard"
        >
            {/* Header */}
            <div className="mb-8">
                <div className="flex items-center gap-3 mb-2">
                    <BarChart3 className="w-8 h-8 text-orange-600" />
                    <h1 className="text-3xl font-bold text-gray-900">
                        Performance Dashboard
                    </h1>
                </div>
                <p className="text-gray-600">
                    Monitor team performance and manage incentives
                </p>
            </div>

            <div className="w-full bg-white border-b mb-6">
                <div className="flex gap-6 px-6 py-4">
                    <button
                        onClick={() => setActiveTab("requests")}
                        className={`pb-2 font-medium ${activeTab === "requests"
                            ? "border-b-2 bg-white border-orange-600 text-orange-600"
                            : "text-gray-500"
                            }`}
                        variant="link"
                    >
                        Requests & Incentives
                    </button>
                    <button
                        onClick={() => setActiveTab("sales")}
                        className={`pb-2 font-medium ${activeTab === "sales"
                            ? "border-b-2 bg-white border-blue-600 text-blue-600"
                            : "text-gray-500"
                            }`}
                    >
                        Sales Team
                    </button>

                    <button
                        onClick={() => setActiveTab("operations")}
                        className={`pb-2 font-medium ${activeTab === "operations"
                            ? "border-b-2 bg-white border-purple-600 text-purple-600"
                            : "text-gray-500"
                            }`}
                    >
                        Operations Team
                    </button>

                    <button
                        onClick={() => setActiveTab("payments_list")}
                        className={`pb-2 font-medium ${activeTab === "payments_list"
                            ? "border-b-2 bg-white border-green-600 text-green-600"
                            : "text-gray-500"
                            }`}
                    >
                        Payments List
                    </button>
                    <button
                        onClick={() => setActiveTab("invoice_list")}
                        className={`pb-2 font-medium ${activeTab === "invoice_list"
                            ? "border-b-2 bg-white border-blue-300 text-blue-600"
                            : "text-gray-500"
                            }`}
                    >
                        Invoice List
                    </button>

                    <button
                        onClick={() => setActiveTab("incentives_list")}
                        className={`pb-2 font-medium ${activeTab === "incentives_list"
                            ? "border-b-2 bg-white border-yellow-600 text-yellow-600"
                            : "text-gray-500"
                            }`}
                    >
                        Incentive List
                    </button>
                </div>
            </div>

            {/* Overall Stats */}
            {/* <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <Card className="border-l-4 border-l-green-600 hover:shadow-lg transition-shadow">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-2">
              <DollarSign className="w-4 h-4" />
              Total Revenue
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-gray-900">
              ₹{(totalRevenue / 100000).toFixed(2)}L
            </div>
            <p className="text-xs text-gray-500 mt-2">This month</p>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-blue-600 hover:shadow-lg transition-shadow">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-2">
              <TrendingUp className="w-4 h-4" />
              Avg Conversion
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-gray-900">
              {avgConversionRate.toFixed(1)}%
            </div>
            <p className="text-xs text-gray-500 mt-2">Sales team average</p>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-purple-600 hover:shadow-lg transition-shadow">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-2">
              <CheckCircle className="w-4 h-4" />
              Completion Rate
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-gray-900">
              {avgCompletionRate.toFixed(1)}%
            </div>
            <p className="text-xs text-gray-500 mt-2">Operations average</p>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-orange-600 hover:shadow-lg transition-shadow">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-2">
              <Award className="w-4 h-4" />
              Incentives Paid
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-gray-900">
              ₹{(totalIncentivesPaid / 1000).toFixed(0)}K
            </div>
            <p className="text-xs text-gray-500 mt-2">Total this month</p>
          </CardContent>
        </Card>
      </div> */}

            {activeTab === "sales" && (
                <div className="w-full min-h-screen">
                    <Card className="mb-8">
                        <CardHeader className="bg-gradient-to-r border-b">
                            <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4">
                                {/* Right Section - Search */}
                                <div className="w-full">
                                    <Input
                                        type="text"
                                        placeholder="Search by sales person name..."
                                        className="w-full px-4 py-2 rounded-lg border border-gray-300 
                   focus:outline-none focus:ring-2
                   transition"
                                        value={searchTerm}
                                        onChange={(e) =>
                                            setSearchTerm(e.target.value)
                                        }
                                    />
                                </div>
                            </div>
                        </CardHeader>
                        <CardContent className="pt-6">
                            <div className="overflow-x-auto">
                                {salesLoading && (
                                    <div className="flex justify-center items-center py-10">
                                        <span className="text-gray-500">
                                            Loading sales data...
                                        </span>
                                    </div>
                                )}

                                {!salesLoading &&
                                    !salesError &&
                                    salespeople.length === 0 && (
                                        <div className="flex justify-center items-center py-10">
                                            <span className="text-gray-400">
                                                No operations team data
                                                available.
                                            </span>
                                        </div>
                                    )}

                                {/* ❌ Error State */}
                                {salesError && (
                                    <div className="flex justify-center items-center py-10">
                                        <span className="text-red-500">
                                            Failed to load data. Please try
                                            again.
                                        </span>
                                    </div>
                                )}
                                {!salesLoading &&
                                    !salesError &&
                                    salespeople.length > 0 && (
                                        <Table>
                                            <TableHeader>
                                                <TableRow>
                                                    <TableHead>Name</TableHead>
                                                    <TableHead>
                                                        Requests
                                                    </TableHead>
                                                    <TableHead>
                                                        Conversion
                                                    </TableHead>
                                                    <TableHead>
                                                        Revenue
                                                    </TableHead>
                                                    {/* <TableHead>Target</TableHead>
                  <TableHead>Rating</TableHead> */}
                                                    <TableHead>
                                                        Incentives
                                                    </TableHead>
                                                    <TableHead>
                                                        Pending Incentives
                                                    </TableHead>
                                                    {/* <TableHead className="text-right">Actions</TableHead> */}
                                                </TableRow>
                                            </TableHeader>
                                            <TableBody>
                                                {filteredSales.map((sp) => (
                                                    <TableRow
                                                        key={sp.id}
                                                        data-testid={`salesperson-row-${sp.id}`}
                                                    >
                                                        <TableCell>
                                                            <div>
                                                                <div className="font-medium text-gray-900">
                                                                    {sp.name}
                                                                </div>
                                                                <div className="text-xs text-gray-500">
                                                                    {sp.email}
                                                                </div>
                                                            </div>
                                                        </TableCell>
                                                        <TableCell>
                                                            {sp.total_requests}
                                                        </TableCell>
                                                        <TableCell>
                                                            <div className="flex items-center gap-2">
                                                                <div className="w-16 bg-gray-200 rounded-full h-2">
                                                                    <div
                                                                        className="bg-green-600 h-2 rounded-full"
                                                                        style={{
                                                                            width: `${getOperationCompletionRate(sp)}%`,
                                                                        }}
                                                                    ></div>
                                                                </div>
                                                                <span className="text-sm font-medium">
                                                                    {getOperationCompletionRate(
                                                                        sp,
                                                                    )}
                                                                    %
                                                                </span>
                                                            </div>
                                                        </TableCell>
                                                        <TableCell className="font-medium text-green-700">
                                                            ₹
                                                            {sp.total_revenue >
                                                                100000
                                                                ? `${(sp.total_revenue / 100000).toFixed(2)}L`
                                                                : sp.total_revenue}
                                                        </TableCell>
                                                        <TableCell className="font-medium text-orange-600">
                                                            ₹
                                                            {sp.incentive_earned >
                                                                1000
                                                                ? `${(sp.incentive_earned / 1000).toFixed(2)}K`
                                                                : sp.incentive_earned}
                                                        </TableCell>

                                                        <TableCell className="font-medium text-orange-600">
                                                            ₹
                                                            {sp.pending_incentive >
                                                                1000
                                                                ? `${(sp.pending_incentive / 1000).toFixed(0)}K`
                                                                : sp.pending_incentive}
                                                        </TableCell>
                                                        {/* <TableCell className="text-right"> */}
                                                        {/* <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveMember(sp.id, "sales")}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        data-testid={`remove-salesperson-${sp.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </TableCell> */}
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    )}
                            </div>
                        </CardContent>
                    </Card>
                </div>
            )}

            {activeTab === "operations" && (
                <div className="w-full min-h-screen">
                    <Card className="mb-8">
                        <CardHeader className="bg-gradient-to-r border-b">
                            <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4">
                                {/* Right Section - Search */}
                                <div className="w-full">
                                    <Input
                                        type="text"
                                        placeholder="Search by operations team name..."
                                        className="w-full px-4 py-2 rounded-lg border border-gray-300 
                   focus:outline-none focus:ring-2
                   transition"
                                        value={searchTerm}
                                        onChange={(e) =>
                                            setSearchTerm(e.target.value)
                                        }
                                    />
                                </div>
                            </div>
                        </CardHeader>
                        <CardContent className="pt-6">
                            <div className="overflow-x-auto">
                                {loading && (
                                    <div className="flex justify-center items-center py-10">
                                        <span className="text-gray-500">
                                            Loading performance data...
                                        </span>
                                    </div>
                                )}

                                {/* ❌ Error State */}
                                {error && (
                                    <div className="flex justify-center items-center py-10">
                                        <span className="text-red-500">
                                            Failed to load data. Please try
                                            again.
                                        </span>
                                    </div>
                                )}

                                {/* 📭 Empty State */}
                                {!loading &&
                                    !error &&
                                    operationsTeam.length === 0 && (
                                        <div className="flex justify-center items-center py-10">
                                            <span className="text-gray-400">
                                                No operations team data
                                                available.
                                            </span>
                                        </div>
                                    )}
                                {!loading &&
                                    !error &&
                                    operationsTeam.length > 0 && (
                                        <Table>
                                            <TableHeader>
                                                <TableRow>
                                                    <TableHead>Name</TableHead>
                                                    <TableHead>
                                                        Processed
                                                    </TableHead>
                                                    <TableHead>
                                                        Completion
                                                    </TableHead>
                                                    <TableHead>
                                                        Total Revenue
                                                    </TableHead>

                                                    {/* <TableHead>Target</TableHead>
                    <TableHead>Rating</TableHead> */}
                                                    <TableHead>
                                                        Incentives
                                                    </TableHead>
                                                    <TableHead>
                                                        Pending Incentives
                                                    </TableHead>
                                                    {/* <TableHead className="text-right">Actions</TableHead> */}
                                                </TableRow>
                                            </TableHeader>
                                            <TableBody>
                                                {filteredOperations.map(
                                                    (op) => (
                                                        <TableRow
                                                            key={op.id}
                                                            data-testid={`operations-row-${op.id}`}
                                                        >
                                                            <TableCell>
                                                                <div>
                                                                    <div className="font-medium text-gray-900">
                                                                        {
                                                                            op.name
                                                                        }
                                                                    </div>
                                                                    <div className="text-xs text-gray-500">
                                                                        {
                                                                            op.email
                                                                        }
                                                                    </div>
                                                                </div>
                                                            </TableCell>
                                                            <TableCell>
                                                                {
                                                                    op.total_requests
                                                                }
                                                            </TableCell>
                                                            <TableCell>
                                                                <div className="flex items-center gap-2">
                                                                    <div className="w-16 bg-gray-200 rounded-full h-2">
                                                                        <div
                                                                            className="bg-purple-600 h-2 rounded-full"
                                                                            style={{
                                                                                width: `${getOperationCompletionRate(op)}%`,
                                                                            }}
                                                                        ></div>
                                                                    </div>
                                                                    <span className="text-sm font-medium">
                                                                        {getOperationCompletionRate(
                                                                            op,
                                                                        )}
                                                                        %
                                                                    </span>
                                                                </div>
                                                            </TableCell>
                                                            <TableCell>
                                                                ₹
                                                                {op.total_revenue >
                                                                    100000
                                                                    ? `${(op.total_revenue / 100000).toFixed(2)}L`
                                                                    : op.total_revenue}
                                                            </TableCell>

                                                            {/* <TableCell>
                        <div className="text-sm">
                          <div className="font-medium">
                            {op.monthlyTarget} requests
                          </div>
                          <div className="text-xs text-gray-500">
                            {(
                              (op.requestsProcessed / op.monthlyTarget) *
                              100
                            ).toFixed(0)}
                            % achieved
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                          <span className="font-medium">
                            {op.customerSatisfaction}
                          </span>
                        </div>
                      </TableCell> */}
                                                            <TableCell className="font-medium text-orange-600">
                                                                ₹
                                                                {op.incentive_earned >
                                                                    1000
                                                                    ? `${(op.incentive_earned / 1000).toFixed(0)}K`
                                                                    : op.incentive_earned}
                                                            </TableCell>
                                                            <TableCell className="font-medium text-orange-600">
                                                                ₹
                                                                {op.pending_incentive >
                                                                    1000
                                                                    ? `${(op.pending_incentive / 1000).toFixed(0)}K`
                                                                    : op.pending_incentive}
                                                            </TableCell>
                                                            {/* <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() =>
                            handleRemoveMember(op.id, "operations")
                          }
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          data-testid={`remove-operations-${op.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </TableCell> */}
                                                        </TableRow>
                                                    ),
                                                )}
                                            </TableBody>
                                        </Table>
                                    )}
                            </div>
                        </CardContent>
                    </Card>
                </div>
            )}

            {activeTab === "requests" && (
                <div className="w-full min-h-screen">
                    <Card>
                        <CardHeader className="bg-gradient-to-r border-b">
                            <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4">
                                {/* Right Section - Search */}
                                <div className="w-full">
                                    <Input
                                        type="text"
                                        placeholder="Search by client name..."
                                        className="w-full px-4 py-2 rounded-lg border border-gray-300 
                   focus:outline-none focus:ring-2
                   transition"
                                        value={searchTerm}
                                        onChange={(e) =>
                                            setSearchTerm(e.target.value)
                                        }
                                    />
                                </div>
                            </div>
                        </CardHeader>
                        <CardContent className="pt-6">
                            <div className="overflow-x-auto">
                                {pendingAndClosureLoading && (
                                    <div className="flex justify-center items-center py-10">
                                        <span className="text-gray-500">
                                            Loading performance data...
                                        </span>
                                    </div>
                                )}

                                {pendingAndClosureError && (
                                    <div className="flex justify-center items-center py-10">
                                        <span className="text-red-500">
                                            Failed to load data. Please try
                                            again.
                                        </span>
                                    </div>
                                )}

                                {!pendingAndClosureLoading &&
                                    !pendingAndClosureError &&
                                    requests.length === 0 && (
                                        <div className="flex justify-center items-center py-10">
                                            <span className="text-gray-400">
                                                No request data available.
                                            </span>
                                        </div>
                                    )}
                                {!pendingAndClosureLoading &&
                                    !pendingAndClosureError &&
                                    requests.length > 0 && (
                                        <Table>
                                            <TableHeader>
                                                <TableRow>
                                                    <TableHead>
                                                        Request ID
                                                    </TableHead>
                                                    <TableHead>
                                                        Customer
                                                    </TableHead>
                                                    {/* 
                  <TableHead>Destination</TableHead> */}
                                                    <TableHead>
                                                        Request Title
                                                    </TableHead>
                                                    <TableHead>
                                                        Assigned To
                                                    </TableHead>
                                                    {/* <TableHead>Revenue</TableHead> */}
                                                    {/* <TableHead>Date</TableHead>
                  <TableHead>Status</TableHead> */}
                                                    <TableHead>
                                                        Invoice
                                                    </TableHead>
                                                    <TableHead className="text-right">
                                                        Actions
                                                    </TableHead>
                                                </TableRow>
                                            </TableHeader>
                                            <TableBody>
                                                {filteredRequests.map((req) => (
                                                    <TableRow
                                                        key={req.request_id}
                                                        data-testid={`request-row-${req.request_id}`}
                                                    >
                                                        <TableCell
                                                            className="font-mono font-medium text-blue-600 cursor-pointer"
                                                            onClick={() => {
                                                                navigate(
                                                                    `/requests/${req.request_id}`,
                                                                );
                                                            }}
                                                        >
                                                            {req.request_id}
                                                        </TableCell>
                                                        <TableCell className="font-medium">
                                                            <div>
                                                                <div className="font-medium text-gray-900">
                                                                    {
                                                                        req.customer_name
                                                                    }
                                                                </div>
                                                                <div className="text-xs text-gray-500">
                                                                    {
                                                                        req.customer_email
                                                                    }
                                                                </div>
                                                            </div>
                                                        </TableCell>
                                                        <TableCell>
                                                            {req.request_title}
                                                        </TableCell>
                                                        <TableCell>
                                                            <div className="flex flex-col space-y-2 text-sm">
                                                                {/* Sales Person */}
                                                                {req.salesperson_name && (
                                                                    <div className="flex items-start justify-between">
                                                                        <div>
                                                                            <p className="font-medium text-gray-900">
                                                                                {
                                                                                    req.salesperson_name
                                                                                }
                                                                            </p>
                                                                            <p className="text-xs text-blue-600 font-medium">
                                                                                Sales
                                                                            </p>
                                                                        </div>
                                                                    </div>
                                                                )}

                                                                {/* Operations Person */}
                                                                {req.operations_name && (
                                                                    <div className="flex items-start justify-between">
                                                                        <div>
                                                                            <p className="font-medium text-gray-900">
                                                                                {
                                                                                    req.operations_name
                                                                                }
                                                                            </p>
                                                                            <p className="text-xs text-purple-600 font-medium">
                                                                                Operations
                                                                            </p>
                                                                        </div>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </TableCell>
                                                        <TableCell className="font-medium text-green-700">
                                                            ₹
                                                            {req.invoice_amount >
                                                                1000
                                                                ? `${(req.invoice_amount / 1000).toFixed(0)}K`
                                                                : req.invoice_amount}
                                                        </TableCell>
                                                        {/* <TableCell className="text-sm text-gray-600">
                      {req.date}
                    </TableCell> */}
                                                        {/* <TableCell>
                      {req.status === "completed" ? (
                        <div>
                          <Badge className="bg-green-100 text-green-800 border-green-300">
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Completed
                          </Badge>
                          {req.incentive && (
                            <div className="text-xs text-gray-600 mt-1">
                              Incentive: ₹{req.incentive}
                            </div>
                          )}
                        </div>
                      ) : (
                        <Badge
                          variant="outline"
                          className="bg-yellow-50 text-yellow-800 border-yellow-300"
                        >
                          <Clock className="w-3 h-3 mr-1" />
                          Pending
                        </Badge>
                      )}
                    </TableCell> */}
                                                        <TableCell className="text-right">
                                                            {req.status !==
                                                                "pending" ? (
                                                                <Button
                                                                    size="sm"
                                                                    onClick={() => {
                                                                        setSelectedRequest(
                                                                            req,
                                                                        );
                                                                        setShowCompleteModal(
                                                                            true,
                                                                        );
                                                                    }}
                                                                    className="bg-green-600 hover:bg-green-700"
                                                                    data-testid={`mark-complete-${req.id}`}
                                                                >
                                                                    <CheckCircle className="w-4 h-4 mr-2" />
                                                                    Mark
                                                                    Complete
                                                                </Button>
                                                            ) : (
                                                                <Badge
                                                                    variant="outline"
                                                                    className="bg-gray-100 text-gray-600"
                                                                >
                                                                    <CheckCircle className="w-3 h-3 mr-1" />
                                                                    Done
                                                                </Badge>
                                                            )}
                                                        </TableCell>

                                                        <TableCell className="text-right">
                                                            <Button
                                                                variant="outline"
                                                                size="sm"
                                                                onClick={(
                                                                    e,
                                                                ) => {
                                                                    e.stopPropagation();
                                                                    navigate(
                                                                        `/requests/${req.request_id}`,
                                                                    );
                                                                }}
                                                            >
                                                                <Eye className="w-4 h-4 mr-2" />
                                                                View
                                                            </Button>
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    )}
                            </div>
                        </CardContent>
                    </Card>
                </div>
            )}

            {activeTab === "payments_list" && (
                <Card>
                    <CardHeader className="bg-gradient-to-r border-b">
                        <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4">
                            {/* Right Section - Search */}
                            <div className="w-full">
                                <Input
                                    type="text"
                                    placeholder="Search by client name..."
                                    className="w-full px-4 py-2 rounded-lg border border-gray-300 
                   focus:outline-none focus:ring-2
                   transition"
                                    value={searchTerm}
                                    onChange={(e) =>
                                        setSearchTerm(e.target.value)
                                    }
                                />
                            </div>
                        </div>
                    </CardHeader>
                    <CardContent className="pt-6">
                        <div className="overflow-x-auto">
                            {paymentsLoading && (
                                <div className="flex justify-center items-center py-10">
                                    <span className="text-gray-500">
                                        Loading invoices data...
                                    </span>
                                </div>
                            )}

                            {paymentsError && (
                                <div className="flex justify-center items-center py-10">
                                    <span className="text-red-500">
                                        Failed to load invoices data
                                    </span>
                                </div>
                            )}
                            {!paymentsLoading &&
                                !paymentsError &&
                                payments.length === 0 && (
                                    <div className="flex justify-center items-center py-10">
                                        <span className="text-gray-500">
                                            No payments found
                                        </span>
                                    </div>
                                )}
                            {!paymentsLoading &&
                                !paymentsError &&
                                payments.length > 0 && (
                                    <Table>
                                        <TableHeader>
                                            <TableRow>
                                                <TableHead>
                                                    Invoice No.
                                                </TableHead>
                                                <TableHead>Client</TableHead>
                                                <TableHead>Amount</TableHead>
                                                <TableHead>
                                                    Payment Method
                                                </TableHead>
                                                <TableHead>Status</TableHead>
                                                <TableHead>
                                                    Received At
                                                </TableHead>
                                            </TableRow>
                                        </TableHeader>

                                        <TableBody>
                                            {filteredPayments?.map(
                                                (payment) => (
                                                    <TableRow key={payment.id}>
                                                        {/* Invoice Number */}
                                                        <TableCell className="font-medium">
                                                            {
                                                                payment.invoice_number
                                                            }
                                                        </TableCell>

                                                        {/* Client Info */}
                                                        <TableCell>
                                                            <div>
                                                                <div className="font-medium text-gray-900">
                                                                    {
                                                                        payment.client_name
                                                                    }
                                                                </div>
                                                                <div className="text-xs text-gray-500">
                                                                    {
                                                                        payment.client_country_code
                                                                    }{" "}
                                                                    {
                                                                        payment.client_phone
                                                                    }
                                                                </div>
                                                                <div className="text-xs text-gray-500">
                                                                    {
                                                                        payment.client_email
                                                                    }
                                                                </div>
                                                            </div>
                                                        </TableCell>

                                                        {/* Amount */}
                                                        <TableCell className="font-semibold">
                                                            ₹
                                                            {payment.amount?.toLocaleString()}
                                                        </TableCell>

                                                        {/* Payment Method */}
                                                        <TableCell className="capitalize">
                                                            {payment.method?.replace(
                                                                "_",
                                                                " ",
                                                            )}
                                                        </TableCell>

                                                        {/* Status */}
                                                        <TableCell>
                                                            <span
                                                                className={`px-2 py-1 text-xs rounded-full capitalize ${payment.status ===
                                                                    "verified_by_ops"
                                                                    ? "bg-green-100 text-green-700"
                                                                    : payment.status ===
                                                                        "pending"
                                                                        ? "bg-yellow-100 text-yellow-700"
                                                                        : "bg-gray-100 text-gray-700"
                                                                    }`}
                                                            >
                                                                {payment.status?.replaceAll(
                                                                    "_",
                                                                    " ",
                                                                )}
                                                            </span>
                                                        </TableCell>

                                                        {/* Received At */}
                                                        <TableCell className="text-sm text-gray-600">
                                                            {new Date(
                                                                payment.received_at,
                                                            ).toLocaleDateString()}
                                                        </TableCell>
                                                    </TableRow>
                                                ),
                                            )}
                                        </TableBody>
                                    </Table>
                                )}
                        </div>
                    </CardContent>
                </Card>
            )}

            {activeTab === "incentives_list" && (
                <Card>
                    {/* <CardHeader className="bg-gradient-to-r from-orange-50 to-orange-100 border-b">
            <div className="flex justify-between items-center">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Target className="w-5 h-5 text-orange-600" />
                  Incentive List
                </CardTitle>
                <CardDescription className="mt-1">
                  View all incentives
                </CardDescription>
              </div>
            </div>
          </CardHeader> */}
                    <CardContent className="pt-6">
                        <div className="overflow-x-auto">
                            {incentivesLoading && (
                                <div className="flex justify-center items-center py-10">
                                    <span className="text-gray-500">
                                        Loading incentives data...
                                    </span>
                                </div>
                            )}

                            {/* ❌ Error State */}
                            {incentivesError && (
                                <div className="flex justify-center items-center py-10">
                                    <span className="text-red-500">
                                        Failed to load incentives data
                                    </span>
                                </div>
                            )}

                            {!incentivesLoading &&
                                !incentivesError &&
                                incentives.length === 0 && (
                                    <div className="flex justify-center items-center py-10">
                                        <span className="text-gray-500">
                                            No incentives found
                                        </span>
                                    </div>
                                )}

                            {!incentivesLoading &&
                                !incentivesError &&
                                incentives.length > 0 && (
                                    <Table>
                                        <TableHeader>
                                            <TableRow>
                                                <TableHead>User</TableHead>
                                                <TableHead>
                                                    Request ID
                                                </TableHead>
                                                <TableHead>Amount</TableHead>
                                                <TableHead>Status</TableHead>
                                                <TableHead>
                                                    Created At
                                                </TableHead>
                                                <TableHead>Paid At</TableHead>
                                                <TableHead className="text-right">
                                                    Action
                                                </TableHead>
                                            </TableRow>
                                        </TableHeader>

                                        <TableBody>
                                            {incentives.map((incentive) => (
                                                <TableRow key={incentive.id}>
                                                    {/* User */}
                                                    <TableCell>
                                                        {incentive.user_id}
                                                    </TableCell>

                                                    {/* Request */}
                                                    <TableCell
                                                        className="font-mono font-medium  cursor-pointer"
                                                        onClick={() => {
                                                            navigate(
                                                                `/requests/${incentive.request_id}`,
                                                            );
                                                        }}
                                                    >
                                                        {incentive.request_id}
                                                    </TableCell>

                                                    {/* Amount */}
                                                    <TableCell className="font-medium text-green-600">
                                                        ₹
                                                        {incentive.amount.toLocaleString()}
                                                    </TableCell>

                                                    {/* Status */}
                                                    <TableCell>
                                                        <span
                                                            className={`px-2 py-1 text-xs rounded-full ${incentive.status ===
                                                                "paid"
                                                                ? "bg-green-100 text-green-700"
                                                                : "bg-yellow-100 text-yellow-700"
                                                                }`}
                                                        >
                                                            {incentive.status}
                                                        </span>
                                                    </TableCell>

                                                    {/* Created At */}
                                                    <TableCell>
                                                        {new Date(
                                                            incentive.created_at,
                                                        ).toLocaleString()}
                                                    </TableCell>

                                                    {/* Paid At */}
                                                    <TableCell>
                                                        {incentive.paid_at
                                                            ? new Date(
                                                                incentive.paid_at,
                                                            ).toLocaleString()
                                                            : "—"}
                                                    </TableCell>

                                                    {/* Action Button */}
                                                    <TableCell className="text-right">
                                                        {incentive.status !==
                                                            "paid" && (
                                                                <button
                                                                    onClick={() => {
                                                                        setSelectedIncentiveId(
                                                                            incentive.id,
                                                                        );
                                                                        setIsModalOpen(
                                                                            true,
                                                                        );
                                                                    }}
                                                                    className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded-md text-sm"
                                                                >
                                                                    Mark as Complete
                                                                </button>
                                                            )}
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                )}
                        </div>
                    </CardContent>
                </Card>
            )}

            {activeTab === "invoice_list" && (
                <Card>
                    <CardHeader className="bg-gradient-to-r border-b">
                        <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4">
                            {/* Right Section - Search */}
                            <div className="w-full">
                                <Input
                                    type="text"
                                    placeholder="Search by client name..."
                                    className="w-full px-4 py-2 rounded-lg border border-gray-300 
                   focus:outline-none focus:ring-2
                   transition"
                                    value={searchTerm}
                                    onChange={(e) =>
                                        setSearchTerm(e.target.value)
                                    }
                                />
                            </div>
                        </div>
                    </CardHeader>
                    <CardContent className="pt-6">
                        <div className="overflow-x-auto">
                            {invoicesLoading && (
                                <div className="flex justify-center items-center py-10">
                                    <span className="text-gray-500">
                                        Loading invoices data...
                                    </span>
                                </div>
                            )}

                            {/* ❌ Error State */}
                            {invoicesError && (
                                <div className="flex justify-center items-center py-10">
                                    <span className="text-red-500">
                                        Failed to load data. Please try again.
                                    </span>
                                </div>
                            )}

                            {!invoicesLoading &&
                                !invoicesError &&
                                invoiceList.length === 0 && (
                                    <div className="flex justify-center items-center py-10">
                                        <span className="text-gray-500">
                                            No invoices found.
                                        </span>
                                    </div>
                                )}
                            {!invoicesLoading &&
                                !invoicesError &&
                                invoiceList.length > 0 && (
                                    <Table>
                                        <TableHeader>
                                            <TableRow>
                                                <TableHead>
                                                    Invoice No.
                                                </TableHead>
                                                <TableHead>Client</TableHead>
                                                <TableHead>
                                                    Total Amount
                                                </TableHead>
                                                <TableHead>Tax</TableHead>
                                                <TableHead>TCS</TableHead>
                                                <TableHead>Status</TableHead>
                                                <TableHead className="text-right">
                                                    Actions
                                                </TableHead>
                                            </TableRow>
                                        </TableHeader>

                                        <TableBody>
                                            {filteredInvoices.map((invoice) => (
                                                <TableRow
                                                    key={invoice.invoice_number}
                                                >
                                                    {/* Invoice Number */}
                                                    <TableCell className="font-medium">
                                                        {invoice.invoice_number}
                                                    </TableCell>

                                                    {/* Client Info */}
                                                    <TableCell>
                                                        <div>
                                                            <div className="font-medium text-gray-900">
                                                                {
                                                                    invoice.client_name
                                                                }
                                                            </div>
                                                            <div className="text-xs text-gray-500">
                                                                {
                                                                    invoice.client_country_code
                                                                }{" "}
                                                                {
                                                                    invoice.client_phone
                                                                }
                                                            </div>
                                                            <div className="text-xs text-gray-500">
                                                                {
                                                                    invoice.client_email
                                                                }
                                                            </div>
                                                        </div>
                                                    </TableCell>

                                                    {/* Total Amount */}
                                                    <TableCell className="font-semibold">
                                                        ₹
                                                        {invoice.total_amount.toLocaleString()}
                                                    </TableCell>

                                                    {/* Tax */}
                                                    <TableCell>
                                                        ₹
                                                        {invoice.tax_amount.toLocaleString()}
                                                    </TableCell>

                                                    {/* TCS */}
                                                    <TableCell>
                                                        ₹
                                                        {invoice.tcs_amount.toLocaleString()}
                                                        {invoice.tcs_percent >
                                                            0 && (
                                                                <span className="text-xs text-gray-500 ml-1">
                                                                    (
                                                                    {
                                                                        invoice.tcs_percent
                                                                    }
                                                                    %)
                                                                </span>
                                                            )}
                                                    </TableCell>

                                                    {/* Status */}
                                                    <TableCell>
                                                        <span
                                                            className={`px-2 py-1 text-xs rounded-full ${invoice.status ===
                                                                "Paid"
                                                                ? "bg-green-100 text-green-700"
                                                                : invoice.status ===
                                                                    "Partially Paid"
                                                                    ? "bg-yellow-100 text-yellow-700"
                                                                    : "bg-red-100 text-red-700"
                                                                }`}
                                                        >
                                                            {invoice.status}
                                                        </span>
                                                    </TableCell>

                                                    {/* Download */}
                                                    <TableCell className="text-right">
                                                        <button
                                                            onClick={() =>
                                                                handleDownload(
                                                                    invoice.invoice_number,
                                                                )
                                                            }
                                                            className="bg-orange-600 text-white px-3 py-1.5 rounded-md hover:bg-orange-700 transition text-sm"
                                                        >
                                                            Download
                                                        </button>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                )}
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Complete Request Modal */}
            <Dialog
                open={showCompleteModal}
                onOpenChange={setShowCompleteModal}
            >
                <DialogContent
                    data-testid="complete-request-modal"
                    className="
      w-full 
      max-w-3xl
      max-h-[90vh] 
      flex 
      flex-col 
      p-0
    "
                >
                    {/* HEADER */}
                    <DialogHeader className="px-6 pt-6 pb-4 border-b">
                        <DialogTitle className="flex items-center gap-2">
                            <Award className="w-5 h-5 text-orange-600" />
                            Mark Request Complete
                        </DialogTitle>
                        <DialogDescription>
                            Assign incentive amount for completing this request
                        </DialogDescription>
                    </DialogHeader>

                    {/* SCROLLABLE BODY */}
                    <div className="flex-1 overflow-y-auto px-6 py-4 space-y-6">
                        {selectedRequest && (
                            <>
                                {/* Request Info Card */}
                                <div className="bg-gray-50 p-4 rounded-lg space-y-2">
                                    <div className="flex justify-between text-sm">
                                        <span className="text-gray-600">
                                            Request ID:
                                        </span>
                                        <span className="font-mono font-medium">
                                            {selectedRequest.request_id}
                                        </span>
                                    </div>

                                    <div className="flex justify-between text-sm">
                                        <span className="text-gray-600">
                                            Customer:
                                        </span>
                                        <span className="font-medium">
                                            {selectedRequest.customer_name}
                                        </span>
                                    </div>

                                    <div className="flex justify-between text-sm">
                                        <span className="text-gray-600">
                                            Invoice:
                                        </span>
                                        <span className="font-medium text-green-700">
                                            ₹
                                            {selectedRequest.invoice_amount.toLocaleString()}
                                        </span>
                                    </div>

                                    <div className="flex justify-between text-sm">
                                        <span className="text-gray-600">
                                            Assigned To:
                                        </span>
                                        <span className="text-sm text-gray-700">
                                            {selectedRequest.salesperson_name && (
                                                <>
                                                    {
                                                        selectedRequest.salesperson_name
                                                    }
                                                    <span className="text-xs text-blue-600 ml-1">
                                                        (Sales)
                                                    </span>
                                                </>
                                            )}

                                            {selectedRequest.salesperson_name &&
                                                selectedRequest.operations_name &&
                                                ", "}

                                            {selectedRequest.operations_name && (
                                                <>
                                                    {
                                                        selectedRequest.operations_name
                                                    }
                                                    <span className="text-xs text-purple-600 ml-1">
                                                        (Operations)
                                                    </span>
                                                </>
                                            )}
                                        </span>
                                    </div>
                                </div>

                                {/* Users Section */}
                                <div className="space-y-4">
                                    <div className="relative w-full">
                                        {/* Search Input */}
                                        <Input
                                            type="text"
                                            placeholder="Search by name or role..."
                                            value={searchTerm}
                                            onChange={(e) => {
                                                setSearchTerm(e.target.value);
                                                setIsDropdownOpen(true);
                                            }}
                                            onFocus={() =>
                                                setIsDropdownOpen(true)
                                            }
                                            className="w-full"
                                        />

                                        {/* Dropdown */}
                                        {isDropdownOpen && searchTerm && (
                                            <div
                                                className="
      absolute z-50
      mt-1 w-full
      bg-white
      border rounded-lg shadow-lg
      max-h-[250px]
      overflow-y-auto
    "
                                            >
                                                {filteredUsers.length > 0 ? (
                                                    filteredUsers.map(
                                                        (user) => (
                                                            <div
                                                                key={
                                                                    user.user_id
                                                                }
                                                                onClick={() => {
                                                                    handleSelectUser(
                                                                        user.user_id,
                                                                    );
                                                                    setSearchTerm(
                                                                        "",
                                                                    );
                                                                    setIsDropdownOpen(
                                                                        false,
                                                                    );
                                                                }}
                                                                className="
              px-4 py-2
              hover:bg-orange-50
              cursor-pointer
              flex justify-between items-center
            "
                                                            >
                                                                <span className="font-medium">
                                                                    {user.name}
                                                                </span>
                                                                <span className="text-xs text-gray-500">
                                                                    {user.role}
                                                                </span>
                                                            </div>
                                                        ),
                                                    )
                                                ) : (
                                                    <div className="px-4 py-2 text-sm text-gray-400">
                                                        No users found
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>

                                    {selectedIncentives.map((user) => {
                                        const userDetails = findUser(
                                            user.user_id,
                                        );
                                        if (!userDetails) return null;

                                        return (
                                            <div
                                                key={user.user_id}
                                                className="
        bg-orange-50
        rounded-lg
        p-4
        flex flex-col sm:flex-row
        sm:items-center
        sm:justify-between
        gap-4
      "
                                            >
                                                {/* Left: Name + Role */}
                                                <div>
                                                    <p className="font-medium text-gray-900">
                                                        {userDetails.name}
                                                    </p>
                                                    <p className="text-xs text-gray-600">
                                                        {userDetails.role}
                                                    </p>
                                                </div>

                                                {/* Middle: Incentive Input */}
                                                <div className="w-full sm:w-40">
                                                    <Label className="text-xs">
                                                        Incentive Amount
                                                    </Label>
                                                    <Input
                                                        type="number"
                                                        value={
                                                            user.incentiveAmount
                                                        }
                                                        onChange={(e) =>
                                                            handleIncentiveChange(
                                                                user.user_id,
                                                                e.target.value,
                                                            )
                                                        }
                                                        className="mt-1 w-full"
                                                    />
                                                </div>

                                                {/* Right: Remove */}
                                                <button
                                                    onClick={() =>
                                                        handleRemoveUser(
                                                            user.user_id,
                                                        )
                                                    }
                                                    className="
          text-red-500
          text-sm
          hover:text-red-700
          self-start sm:self-center
        "
                                                >
                                                    Remove
                                                </button>
                                            </div>
                                        );
                                    })}
                                </div>
                            </>
                        )}
                    </div>

                    {/* FOOTER */}
                    <DialogFooter className="px-6 py-4 border-t bg-white">
                        <Button
                            variant="outline"
                            onClick={() => {
                                setShowCompleteModal(false);
                                setSelectedRequest(null);
                            }}
                        >
                            Cancel
                        </Button>

                        <Button
                            onClick={handleMarkComplete}
                            disabled={
                                isSubmitting
                            }
                            className={`
    bg-green-600 
    hover:bg-green-700 
    disabled:bg-gray-400 
    disabled:cursor-not-allowed
  `}
                        >
                            {isSubmitting ? (
                                <>
                                    <svg
                                        className="animate-spin h-4 w-4 mr-2"
                                        viewBox="0 0 24 24"
                                    >
                                        <circle
                                            className="opacity-25"
                                            cx="12"
                                            cy="12"
                                            r="10"
                                            stroke="currentColor"
                                            strokeWidth="4"
                                            fill="none"
                                        />
                                        <path
                                            className="opacity-75"
                                            fill="currentColor"
                                            d="M4 12a8 8 0 018-8v8z"
                                        />
                                    </svg>
                                    Processing...
                                </>
                            ) : (
                                <>
                                    <CheckCircle className="w-4 h-4 mr-2" />
                                    Confirm Complete
                                </>
                            )}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Add Team Member Modal */}
            <Dialog
                open={showAddMemberModal}
                onOpenChange={setShowAddMemberModal}
            >
                <DialogContent data-testid="add-member-modal">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <UserPlus className="w-5 h-5 text-orange-600" />
                            Add Team Member
                        </DialogTitle>
                        <DialogDescription>
                            Add a new{" "}
                            {newMember.role === "sales"
                                ? "salesperson"
                                : "operations"}{" "}
                            team member
                        </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-4">
                        <div className="space-y-2">
                            <Label htmlFor="member-role">Role</Label>
                            <select
                                id="member-role"
                                value={newMember.role}
                                onChange={(e) =>
                                    setNewMember({
                                        ...newMember,
                                        role: e.target.value,
                                    })
                                }
                                className="w-full p-2 border rounded-md"
                                data-testid="member-role-select"
                            >
                                <option value="sales">Sales</option>
                                <option value="operations">Operations</option>
                            </select>
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="member-name">Full Name</Label>
                            <Input
                                id="member-name"
                                placeholder="Enter full name"
                                value={newMember.name}
                                onChange={(e) =>
                                    setNewMember({
                                        ...newMember,
                                        name: e.target.value,
                                    })
                                }
                                data-testid="member-name-input"
                            />
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="member-email">Email</Label>
                            <Input
                                id="member-email"
                                type="email"
                                placeholder="Enter email address"
                                value={newMember.email}
                                onChange={(e) =>
                                    setNewMember({
                                        ...newMember,
                                        email: e.target.value,
                                    })
                                }
                                data-testid="member-email-input"
                            />
                        </div>
                    </div>

                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => {
                                setShowAddMemberModal(false);
                                setNewMember({
                                    name: "",
                                    email: "",
                                    role: "sales",
                                });
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleAddMember}
                            className="bg-orange-600 hover:bg-orange-700"
                            data-testid="confirm-add-member-btn"
                        >
                            <Plus className="w-4 h-4 mr-2" />
                            Add Member
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {isModalOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg shadow-lg w-[400px] p-6">
                        <h2 className="text-lg font-semibold mb-4">
                            Confirm Completion
                        </h2>

                        <p className="text-sm text-gray-600 mb-6">
                            Do you want to complete this incentive?
                        </p>

                        <div className="flex justify-end gap-3">
                            {/* Cancel */}
                            <button
                                onClick={() => {
                                    setIsModalOpen(false);
                                    setSelectedIncentiveId(null);
                                }}
                                className="px-4 py-2 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-100"
                            >
                                Cancel
                            </button>

                            {/* Confirm */}
                            <button
                                onClick={handleMarkCompleteForIncentive}
                                className="px-4 py-2 rounded-md bg-blue-600 text-white hover:bg-blue-700"
                            >
                                Confirm
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminPerformanceDashboard;
